import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, keyframes, styled, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { AgentActivityAtom, pickActivityLabel } from '../hooks/agentActivityAtom';
import { useDwelledLabel } from '../hooks/useDwelledLabel';

const gradientShift = keyframes`
  0% {
    background-position: 0 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
`;

const ActivityRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  min-height: 32px;
`;

const ActivityLabelText = styled(Typography)`
  display: flex;
  background: linear-gradient(142.43deg, #1bc8ff 35.03%, #9789ff 69.24%, #1bc8ff 100%);
  background-size: 200% 100%;
  background-clip: text;
  color: transparent;
  animation: ${gradientShift} 2s ease-in-out infinite;
  @media (max-width: 768px) {
    &&& {
      font-size: 110%;
    }
  }
`;

interface ActivityIndicatorProps {
  /** A generation turn is in flight: show the "Thinking..." fallback when no
   *  label is live, and hold a vanished label for its minimum dwell. */
  isTyping?: boolean;
}

export const ThinkingLabel = 'Thinking...';

/**
 * The "what's happening right now" line, Claude-Desktop style: the assistant
 * sparkle always leads it. Next to the sparkle, one animated gradient text:
 * the current `agent_activity` label when one is live (a tool call this turn,
 * or an ambient background agent like Atlas), "Thinking..." while a turn is in
 * flight with no reported activity, and nothing at all when idle — the sparkle
 * alone marks that the assistant is waiting for the next input.
 */
export function ActivityIndicator({ isTyping = false }: ActivityIndicatorProps) {
  const activity = useAtomValue(AgentActivityAtom);
  const label = useDwelledLabel(pickActivityLabel(activity), isTyping);
  const text = label ?? (isTyping ? ThinkingLabel : undefined);

  return (
    <ActivityRow role='status' aria-live='polite'>
      <AutoAwesomeIcon color='primary' fontSize='small' />
      {text && <ActivityLabelText>{text}</ActivityLabelText>}
    </ActivityRow>
  );
}
